import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { resolveDashboardMode } from "@/lib/dashboard-mode";
import { useConfirm } from "@/lib/confirm";
import { getRoleLabel } from "./role-meta";
import { useEmAtendimento } from "@/lib/em-atendimento";
import { TeamMembersToolbar, type RoleOption } from "./TeamMembersToolbar";
import { TeamMemberRow } from "./TeamMemberRow";
import { TeamMemberCard } from "./TeamMemberCard";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { TeamMemberDrawer } from "./TeamMemberDrawer";
import { WORKLOAD_COLUMN_HINT } from "./workload-meta";
import { ehDonoOuSocio } from "@bookdental/schemas";

type Props = {
  /** Só ADMIN/OWNER veem os controles de gestão (trocar role, suspender, remover). */
  canManage: boolean;
};

export function TeamMembersList({ canManage }: Props) {
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);
  useParametroDaUrl("abrir", setOpenMemberId);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const utils = trpc.useUtils();
  const membersQuery = trpc.team.list.useQuery();
  const meQuery = trpc.me.current.useQuery();
  const viewerIsOwner = ehDonoOuSocio(meQuery.data?.activeOrganization?.role);
  const mode = resolveDashboardMode(meQuery.data?.activeOrganization?.types ?? []);
  // Só a clínica atende paciente — no laboratório o selo não teria o que mostrar.
  const { porDentista: emAtendimento } = useEmAtendimento(mode !== "lab");
  const customRolesQuery = trpc.organization.customRoles.list.useQuery(undefined, {
    enabled: viewerIsOwner,
  });
  const customRoles = customRolesQuery.data ?? [];
  // Carga aberta muda por fora desta tela o tempo todo (consulta criada na Agenda, trabalho
  // concluído na Produção) — refetch em mount/foco em vez de invalidar a partir de cada mutation
  // de agenda/produção, que espalharia esse acoplamento por dezenas de call sites pra um número
  // que só existe aqui. Mesmo padrão de useFeatureFlags (apps/app/src/lib/use-feature.ts).
  const workloadQuery = trpc.team.workload.useQuery(undefined, {
    enabled: canManage,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const confirm = useConfirm();
  const invalidate = () => {
    utils.team.list.invalidate();
    utils.me.current.invalidate();
  };

  const updateRole = trpc.team.updateRole.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Cargo atualizado.");
    },
    onError: (err) => toast.error(err.message),
  });
  const assignCustomRole = trpc.team.assignCustomRole.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Cargo atualizado.");
    },
    onError: (err) => toast.error(err.message),
  });
  // Trocar cargo muda o que a pessoa enxerga e pode fazer na organização inteira, e era a única
  // ação administrativa que disparava direto do `select` — suspender e remover já confirmavam.
  const handleRoleChange = async (membershipId: string, value: string) => {
    const membro = ((membersQuery.data ?? []) as any[]).find((m) => m.id === membershipId);
    const nome = membro?.user?.name || membro?.user?.email || "esse membro";
    const novoRotulo = value.startsWith("custom:")
      ? (customRoles.find((r: any) => r.id === value.slice("custom:".length))?.label ?? "papel customizado")
      : getRoleLabel(value.slice("role:".length), mode);
    const ok = await confirm({
      title: "Alterar o cargo?",
      description: (
        <span>
          <strong className="text-zinc-800 dark:text-foreground">{nome}</strong> passa a ser{" "}
          <strong className="text-zinc-800 dark:text-foreground">{novoRotulo}</strong>. As permissões
          mudam na hora, em toda a organização.
        </span>
      ),
      confirmText: "Alterar cargo",
    });
    if (!ok) return;
    if (value.startsWith("custom:")) {
      assignCustomRole.mutate({ membershipId, customRoleId: value.slice("custom:".length) });
    } else {
      updateRole.mutate({ membershipId, role: value.slice("role:".length) as any });
    }
  };
  const suspend = trpc.team.suspend.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Acesso suspenso.");
    },
    onError: (err) => toast.error(err.message),
  });
  const reactivate = trpc.team.reactivate.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Acesso reativado.");
    },
    onError: (err) => toast.error(err.message),
  });
  const remove = trpc.team.remove.useMutation({
    onSuccess: () => {
      invalidate();
      toast.success("Membro removido.");
    },
    onError: (err) => toast.error(err.message),
  });

  const members = useMemo(() => (membersQuery.data ?? []) as any[], [membersQuery.data]);

  const roleOptions: RoleOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of members) {
      const key = m.customRoleId ? `custom:${m.customRoleId}` : `role:${m.role}`;
      const label = m.customRole?.label ?? getRoleLabel(m.role, mode);
      if (!seen.has(key)) seen.set(key, label);
    }
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [members, mode]);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return members.filter((m) => {
      if (term) {
        const haystack = `${m.user.name ?? ""} ${m.user.email ?? ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (roleFilter) {
        const key = m.customRoleId ? `custom:${m.customRoleId}` : `role:${m.role}`;
        if (key !== roleFilter) return false;
      }
      if (statusFilter && m.status !== statusFilter) return false;
      return true;
    });
  }, [members, search, roleFilter, statusFilter]);

  const hasActiveFilters = !!(search || roleFilter || statusFilter);
  const clearFilters = () => {
    setSearch("");
    setRoleFilter("");
    setStatusFilter("");
  };

  if (membersQuery.isLoading) {
    return <p className="px-6 py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">Carregando...</p>;
  }

  return (
    <>
      <TeamMembersToolbar
        search={search}
        onSearchChange={setSearch}
        roleFilter={roleFilter}
        onRoleFilterChange={setRoleFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        roleOptions={roleOptions}
        hasActiveFilters={hasActiveFilters}
        onClear={clearFilters}
      />

      {filteredMembers.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500 dark:text-muted-foreground">
          {hasActiveFilters ? "Nenhum membro encontrado com esses filtros." : "Nenhum membro ainda."}
        </p>
      ) : (
        <>
          {/* Celular: cartões. A tabela abaixo continua idêntica no desktop — o bloco novo é
              aditivo, não uma reescrita responsiva por dentro. */}
          <ul className="divide-y divide-zinc-100 md:hidden dark:divide-border">
            {filteredMembers.map((m) => (
              <TeamMemberCard
                key={m.id}
                member={m}
                mode={mode}
                atendimento={emAtendimento.get(m.user.id)}
                canManage={canManage}
                viewerIsOwner={viewerIsOwner}
                customRoles={customRoles}
                rolePending={updateRole.isPending || assignCustomRole.isPending}
                suspendPending={suspend.isPending}
                reactivatePending={reactivate.isPending}
                removePending={remove.isPending}
                onOpenProfile={setOpenMemberId}
                onRoleChange={handleRoleChange}
                onSuspend={(mm) => suspend.mutate({ id: mm.id })}
                onReactivate={(mm) => reactivate.mutate({ id: mm.id })}
                onRemove={(mm) => remove.mutate({ id: mm.id })}
              />
            ))}
          </ul>

          <Table className="hidden md:table">
          <TableHeader>
            <TableRow>
              <TableHead>Membro</TableHead>
              <TableHead>Acesso</TableHead>
              <TableHead>Responsabilidades</TableHead>
              <TableHead title={WORKLOAD_COLUMN_HINT[mode]}>Carga atual</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredMembers.map((m) => (
              <TeamMemberRow
                key={m.id}
                member={m}
                mode={mode}
                atendimento={emAtendimento.get(m.user.id)}
                workload={workloadQuery.data}
                canManage={canManage}
                viewerIsOwner={viewerIsOwner}
                customRoles={customRoles}
                rolePending={updateRole.isPending || assignCustomRole.isPending}
                suspendPending={suspend.isPending}
                reactivatePending={reactivate.isPending}
                removePending={remove.isPending}
                onOpenProfile={setOpenMemberId}
                onRoleChange={handleRoleChange}
                onSuspend={(mm) => suspend.mutate({ id: mm.id })}
                onReactivate={(mm) => reactivate.mutate({ id: mm.id })}
                onRemove={(mm) => remove.mutate({ id: mm.id })}
              />
            ))}
          </TableBody>
          </Table>
        </>
      )}

      <TeamMemberDrawer membershipId={openMemberId} onClose={() => setOpenMemberId(null)} />
    </>
  );
}
